import { CLIENT_LIFECYCLE, CustomerKey, checkFieldValue, } from './clients.js';
import { INDUSTRIES } from './controls.js';
/**
 * Clients by CSV (task 67, decision 0043). Export and import use one format, so a sheet exported, edited
 * and imported again changes only what was edited. An import is planned first: every row is a new client,
 * an update (with what changes), unchanged, or an error in words, and nothing is saved until the person
 * confirms the plan they saw.
 *
 * Columns: the standard ones below by their header, then one per custom field by its key. A blank cell
 * leaves the value as it is; "-" clears it. Dates are YYYY-MM-DD, yes/no fields yes or no, amounts plain
 * numbers ("12,500" and "RM 12,500" are read too).
 */
export const CSV_COLUMNS = ['customer_key', 'customer_name', 'stage', 'policy_number', 'line_of_business', 'premium', 'sum_insured', 'renewal_date', 'broker', 'industry', 'country']; // prettier-ignore
/** Read-only columns an export adds after the custom fields, ignored on import. */
export const CSV_INFO_COLUMNS = ['assessments', 'latest_score', 'latest_band', 'evidence_grade', 'updated_at']; // prettier-ignore
export const MAX_IMPORT_ROWS = 2000;
export const MAX_IMPORT_BYTES = 2_000_000;
export const CLEAR = '-';
// —— CSV text ——
/** RFC 4180: quoted fields, doubled quotes, CRLF or LF, a leading byte-order mark. */
export function parseCsv(text) {
    const s = text.replace(/^\uFEFF/, '');
    const rows = [];
    let row = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < s.length; i++) {
        const c = s[i];
        if (quoted) {
            if (c === '"' && s[i + 1] === '"') {
                cell += '"';
                i++;
            }
            else if (c === '"')
                quoted = false;
            else
                cell += c;
        }
        else if (c === '"' && cell === '')
            quoted = true;
        else if (c === ',') {
            row.push(cell);
            cell = '';
        }
        else if (c === '\n' || c === '\r') {
            if (c === '\r' && s[i + 1] === '\n')
                i++;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
        }
        else
            cell += c;
    }
    if (cell !== '' || row.length) {
        row.push(cell);
        rows.push(row);
    }
    // Blank lines (an empty cell alone) are skipped.
    return rows.filter((r) => r.some((c) => c.trim() !== ''));
}
/**
 * A cell a spreadsheet won't run as a formula: text starting with = + - @ or a tab gets a leading
 * apostrophe, which import takes off again. "-" alone (clear) and plain numbers are left as they are.
 */
const guard = (v) => /^[=+\-@\t\r]/.test(v) && v !== CLEAR && !/^-?\d+(\.\d+)?$/.test(v) ? `'${v}` : v;
const unguard = (v) => (/^'[=+\-@\t\r]/.test(v) ? v.slice(1) : v);
export function csvText(rows) {
    const cell = (v) => {
        const t = guard(v === null || v === undefined ? '' : typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v));
        return /[",\r\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    return rows.map((r) => r.map(cell).join(',')).join('\r\n') + '\r\n';
}
export const csvHeader = (fields) => [...CSV_COLUMNS, ...fields.map((f) => f.key), ...CSV_INFO_COLUMNS]; // prettier-ignore
export function exportCsv(rows, fields) {
    return csvText([
        csvHeader(fields),
        ...rows.map(({ record: r, ...info }) => [
            r.customer_key, r.customer_name, r.lifecycle, r.policy_number, r.line_of_business, r.premium, r.sum_insured,
            r.renewal_date, r.broker, r.industry, r.country,
            ...fields.map((f) => r.custom[f.key]),
            info.assessments, info.latest_score, info.latest_band, info.evidence_grade, info.updated_at,
        ]),
    ]); // prettier-ignore
}
const LIFECYCLE_ALIASES = Object.fromEntries(CLIENT_LIFECYCLE.flatMap((s) => [[s, s], [s.toUpperCase(), s]])); // prettier-ignore
const INDUSTRY_BY_LABEL = Object.fromEntries(Object.entries(INDUSTRIES).flatMap(([k, l]) => [[k, k], [String(l).toLowerCase(), k]])); // prettier-ignore
const amount = (t) => {
    const n = Number(t.replace(/[,\s]|^RM/gi, ''));
    return Number.isFinite(n) && n >= 0 && n <= 1e12 ? Math.round(n * 100) / 100 : null;
};
const realDate = (s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};
/** One standard cell as stored, null to clear, or an error. */
function standardCell(col, t) {
    // prettier-ignore
    if (t === CLEAR)
        return col === 'customer_name' || col === 'stage' ? { ok: false, message: 'can’t be cleared' } : { ok: true, value: null }; // prettier-ignore
    switch (col) {
        case 'customer_name':
            return t.length <= 200 ? { ok: true, value: t } : { ok: false, message: 'up to 200 characters' };
        case 'stage': {
            const s = LIFECYCLE_ALIASES[t.toLowerCase()];
            return s ? { ok: true, value: s } : { ok: false, message: `one of ${CLIENT_LIFECYCLE.join(', ')}` };
        }
        case 'premium':
        case 'sum_insured': {
            const n = amount(t);
            return n === null ? { ok: false, message: 'an amount in ringgit' } : { ok: true, value: n };
        }
        case 'renewal_date':
            return realDate(t) ? { ok: true, value: t } : { ok: false, message: 'a date as YYYY-MM-DD' };
        case 'industry': {
            const k = INDUSTRY_BY_LABEL[t.toLowerCase()];
            return k ? { ok: true, value: k } : { ok: false, message: 'not an industry this app knows' };
        }
        case 'country':
            return /^[A-Za-z]{2}$/.test(t) ? { ok: true, value: t.toUpperCase() } : { ok: false, message: 'two letters, e.g. MY' }; // prettier-ignore
        case 'policy_number':
            return t.length <= 60 ? { ok: true, value: t } : { ok: false, message: 'up to 60 characters' };
        case 'line_of_business':
            return t.length <= 100 ? { ok: true, value: t } : { ok: false, message: 'up to 100 characters' };
        case 'broker':
            return t.length <= 200 ? { ok: true, value: t } : { ok: false, message: 'up to 200 characters' };
    }
}
const TO_FIELD = {
    customer_name: 'customer_name',
    stage: 'lifecycle',
    policy_number: 'policy_number',
    line_of_business: 'line_of_business',
    premium: 'premium',
    sum_insured: 'sum_insured',
    renewal_date: 'renewal_date',
    broker: 'broker',
    industry: 'industry',
    country: 'country',
};
/** A short, stable fingerprint (FNV-1a, 2 × 32 bits); not for security, only to spot a changed plan. */
export function fingerprint(s) {
    let a = 0x811c9dc5;
    let b = 0x01000193;
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        a = Math.imul(a ^ c, 0x01000193) >>> 0;
        b = Math.imul(b ^ c, 0x5bd1e995) >>> 0;
    }
    return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}
/**
 * The plan for a CSV against the clients the person sees (`existing`, by key, with their revision) and
 * the tenant's custom fields. Pure: the server runs it for the preview and again, on the same data, to
 * save what the preview showed.
 */
export function planImport(text, existing, fields) {
    // prettier-ignore
    const empty = (file_issues) => ({ rows: [], counts: { create: 0, update: 0, unchanged: 0, error: 0 }, file_issues, digest: '' }); // prettier-ignore
    if (text.length > MAX_IMPORT_BYTES)
        return empty([`The file is over ${MAX_IMPORT_BYTES / 1_000_000} MB`]);
    const table = parseCsv(text);
    if (!table.length)
        return empty(['The file is empty']);
    const header = table[0].map((h) => h.trim());
    const byField = new Map(fields.flatMap((f) => [
        [f.key, f],
        [f.label.toLowerCase(), f],
    ]));
    const issues = [];
    const cols = header.map((h) => {
        if (CSV_COLUMNS.includes(h))
            return { h, kind: 'std' };
        if (CSV_INFO_COLUMNS.includes(h))
            return { h, kind: 'info' };
        const f = byField.get(h) ?? byField.get(h.toLowerCase());
        if (f)
            return { h, kind: 'custom', f };
        if (h)
            issues.push(`Unknown column “${h}”: use the export’s headers, or add it as a client field first`);
        return { h, kind: 'info' };
    });
    if (!header.includes('customer_key'))
        issues.push('The file needs a customer_key column');
    const dup = header.find((h, i) => h && header.indexOf(h) !== i);
    if (dup)
        issues.push(`The column “${dup}” is there twice`);
    if (table.length - 1 > MAX_IMPORT_ROWS)
        issues.push(`Up to ${MAX_IMPORT_ROWS} clients a file; this has ${table.length - 1}`); // prettier-ignore
    if (issues.length)
        return empty(issues);
    const lower = new Map([...existing.keys()].map((k) => [k.toLowerCase(), k]));
    const seen = new Map();
    const rows = table.slice(1).map((cells, i) => {
        const line = i + 2;
        const at = (h) => unguard((cells[header.indexOf(h)] ?? '').trim());
        const key = at('customer_key');
        const plan = { line, customer_key: key, action: 'error', changes: [], issues: [], values: {}, revision: 0 }; // prettier-ignore
        const bad = (column, message) => plan.issues.push({ column, message });
        if (cells.length > header.length && cells.slice(header.length).some((c) => c.trim()))
            bad(null, 'More cells than columns');
        const k = CustomerKey.safeParse(key);
        if (!k.success) {
            bad('customer_key', key ? (k.error.issues[0]?.message ?? 'not a valid key') : 'needed');
            return plan;
        }
        if (seen.has(key.toLowerCase()))
            bad('customer_key', `already on line ${seen.get(key.toLowerCase())}`);
        seen.set(key.toLowerCase(), line);
        const same = lower.get(key.toLowerCase());
        if (same && same !== key)
            bad('customer_key', `differs only in case from the client ${same}`);
        const current = same === key ? existing.get(key) : null;
        plan.revision = current?.revision ?? 0;
        const custom = {};
        for (const c of cols) {
            if (c.kind === 'info' || c.h === 'customer_key')
                continue;
            const t = at(c.h);
            if (t === '')
                continue; // blank: leave as it is
            if (c.kind === 'std') {
                const r = standardCell(c.h, t);
                if (!r.ok)
                    bad(c.h, r.message);
                else {
                    const f = TO_FIELD[c.h];
                    if (!current || JSON.stringify(current[f] ?? null) !== JSON.stringify(r.value)) {
                        plan.values[f] = r.value;
                        plan.changes.push(c.h);
                    }
                }
            }
            else {
                const r = t === CLEAR ? { ok: true, value: null } : checkFieldValue(c.f, t);
                if (!r.ok)
                    bad(c.h, r.message.replace(`${c.f.label}: `, ''));
                else if (JSON.stringify(current?.custom[c.f.key] ?? null) !== JSON.stringify(r.value)) {
                    custom[c.f.key] = r.value;
                    plan.changes.push(c.h);
                }
            }
        }
        if (Object.keys(custom).length)
            plan.values.custom = custom;
        if (!current && plan.values.customer_name === undefined && !plan.issues.length)
            bad('customer_name', 'needed for a new client'); // prettier-ignore
        plan.action = plan.issues.length
            ? 'error'
            : !current
                ? 'create'
                : plan.changes.length
                    ? 'update'
                    : 'unchanged';
        if (plan.action === 'error')
            plan.values = {};
        return plan;
    });
    const counts = { create: 0, update: 0, unchanged: 0, error: 0 };
    for (const r of rows)
        counts[r.action]++;
    const digest = fingerprint(JSON.stringify(rows.map((r) => [r.line, r.customer_key, r.action, r.revision, r.values])));
    return { rows, counts, file_issues: [], digest };
}
/** The rows that failed, as they were, with a column saying why: fix them and import this file again. */
export function errorReport(text, plan) {
    const table = parseCsv(text);
    const why = new Map(plan.rows.filter((r) => r.action === 'error').map((r) => [r.line, r.issues.map((i) => (i.column ? `${i.column}: ${i.message}` : i.message)).join('; ')])); // prettier-ignore
    return csvText([[...(table[0] ?? []), 'errors'], ...table.slice(1).flatMap((r, i) => (why.has(i + 2) ? [[...r, why.get(i + 2)]] : []))]); // prettier-ignore
}
//# sourceMappingURL=clients-csv.js.map